/**
 * TanStack Query key factory for consistent cache management
 */

export const queryKeys = {
  // Health check
  health: () => ['insight', 'health'] as const,

  // Sessions
  sessions: () => ['insight', 'sessions'] as const,
  session: (id: string) => ['insight', 'sessions', id] as const,
  sessionQuestions: (id: string) => ['insight', 'sessions', id, 'questions'] as const,
  sessionStats: (id: string) => ['insight', 'sessions', id, 'stats'] as const,
  sessionStatus: (id: string) => ['insight', 'sessions', id, 'status'] as const,
  sessionDetails: (id: string) => ['insight', 'sessions', id, 'details'] as const,
  sessionsList: (params?: { status?: string; limit?: number; offset?: number; firm_id?: number | null }) =>
    ['insight', 'sessions', 'list', params] as const,

  // Prepopulation
  prepop: () => ['insight', 'prepop'] as const,
  batchPrepop: () => ['insight', 'prepop', 'batch'] as const,

  // Knowledge Base (Admin)
  kb: () => ['kb'] as const,
  kbEntries: () => ['kb', 'entries'] as const,
  kbEntriesList: (filters?: Record<string, unknown>) => ['kb', 'entries', 'list', filters] as const,
  kbEntry: (id: string) => ['kb', 'entries', id] as const,
  kbStats: (firmScope?: number | null) => ['kb', 'stats', firmScope ?? null] as const,
  kbCategories: () => ['kb', 'categories'] as const,
  kbDuplicates: (questionText: string) => ['kb', 'duplicates', questionText] as const,

  // Vault Narratives (Two-Tier Architecture)
  vaultNarratives: () => ['insight', 'vault', 'narratives'] as const,

  // Coverage Report (F3)
  sessionCoverage: (id: string) => ['insight', 'sessions', id, 'coverage'] as const,

  // Self-Serve Onboarding Readiness (F6)
  onboardingReadiness: () => ['insight', 'onboarding', 'readiness'] as const,

  // Document Workspace (M2): response-action mutations keyed per session, so
  // the workspace can track in-flight updates without colliding with the
  // review feature's keys.
  workspaceResponseAction: (sessionId: string) =>
    ['insight', 'workspace', sessionId, 'response-action'] as const,

  // Document Workspace (M3): the original document prefilled with answers,
  // keyed by session + filter so 'proposed' and 'accepted' cache separately.
  workspaceOriginalDocument: (sessionId: string, filter: 'proposed' | 'accepted') =>
    ['insight', 'workspace', sessionId, 'original-document', filter] as const,

  // Import Review Workbench (Stage 3 — content onboarding)
  importBatches: () => ['import-review', 'batches'] as const,
  importBatchMappings: (batchId: string, status?: string | null) =>
    ['import-review', 'batches', batchId, 'mappings', status ?? null] as const,
  importTopics: (strategyNodeId: number) =>
    ['import-review', 'topics', strategyNodeId] as const,
  importStrategies: () => ['import-review', 'strategies'] as const,
};

export type QueryKeys = typeof queryKeys;
